import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import { useSelector } from "react-redux";
import SubmitButton from "../submitButton/SubmitButton";
import { useNotifications } from "../../context/Notifications";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import { useTranslator } from "context/translator";
import { createCampaignCustomerService } from "services/campaignCustomer/services";
import { getCustomersService } from "services/customers/services";

function CampaignCustomerAddModal({
  getCustomerLists,
  buttonName,
  campaignListId,
}) {
  const { translate } = useTranslator();
  const { user } = useSelector((state) => state.user);

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    { skip: !userProfile?.user?.companyId },
  );

  const { addNotification } = useNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // FIX: store the full selected option, not just the id,
  // so AsyncSelect can display the selected value correctly
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const toggleModal = () => setModalOpen((prev) => !prev);

  const handleCancel = () => {
    setSelectedCustomer(null);
    toggleModal();
  };

  const handleAddCustomerList = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!selectedCustomer?.value) return;

    setIsLoading(true);

    const response = await createCampaignCustomerService({
      data: {
        campaignListId,
        customerId: selectedCustomer.value,
      },
    });

    if (response?.campaignCustomer?.success) {
      addNotification({
        id: Date.now(),
        message: "Customer added to List!",
        kind: "success",
      });
      await getCustomerLists();
      setSelectedCustomer(null);
      toggleModal();
    } else {
      addNotification({
        id: Date.now(),
        message:
          response?.campaignCustomer?.message ||
          "Error adding customer to list!",
        kind: "danger",
      });
    }

    setIsLoading(false);
  };

  const loadOptions = async (inputValue) => {
    const data = await getCustomersService({
      companyId: company?._id,
      page: 1,
      search: inputValue,
      category: "all",
      communications: "all",
      pageSize: 3,
    });

    // Adjust the field names below to match what getCustomersService actually returns
    const list = data?.customers ?? data?.data ?? data ?? [];
    return list.map((c) => ({
      value: c._id ?? c.id,
      label:
        c.name ??
        c.fullName ??
        `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim(),
      email: c.email,
    }));
  };

  // react-select custom styles — only what Tailwind can't target directly
  const selectStyles = {
    control: (base, { isFocused }) => ({
      ...base,
      borderRadius: "10px",
      borderColor: isFocused ? "#1a56db" : "#E2E8F0",
      boxShadow: isFocused ? "0 0 0 3px rgba(26,86,219,0.12)" : "none",
      padding: "2px 4px",
      fontSize: "14px",
      "&:hover": { borderColor: "#1a56db" },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
      border: "1px solid #E2E8F0",
      overflow: "hidden",
      zIndex: 60,
    }),
    option: (base, { isFocused, isSelected }) => ({
      ...base,
      fontSize: "14px",
      backgroundColor: isSelected
        ? "#1a56db"
        : isFocused
          ? "#EFF6FF"
          : "transparent",
      color: isSelected ? "#fff" : "#1E293B",
      cursor: "pointer",
    }),
    placeholder: (base) => ({ ...base, color: "#94A3B8", fontSize: "14px" }),
    singleValue: (base) => ({ ...base, color: "#1E293B", fontSize: "14px" }),
    input: (base) => ({ ...base, color: "#1E293B" }),
    // Custom option with email subtitle — handled via formatOptionLabel below
  };

  if (userLoading || companyLoading) return null;

  return (
    <>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={toggleModal}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-150 hover:-translate-y-px"
        style={{
          background: "rgb(26 86 219 / 0.9)",
          boxShadow: "0 4px 14px rgba(26,86,219,0.3)",
        }}
      >
        <svg
          width="16"
          height="16"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4v16m8-8H4"
          />
        </svg>
        {translate(buttonName || "add-customer")}
      </button>

      {/* Modal Overlay */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: "rgba(15,23,42,0.55)",
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <div
            className="bg-white rounded-2xl overflow-hidden w-full max-w-xl shadow-2xl flex"
            style={{ maxHeight: "90vh" }}
          >
            {/* ── Left Panel ── */}
            <div
              className="flex flex-col justify-between p-10 text-white flex-shrink-0 w-52"
              style={{ background: "rgb(26 86 219 / 0.9)" }}
            >
              <div>
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center mb-5">
                  <svg
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="white"
                    strokeWidth={1.8}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                    />
                  </svg>
                </div>

                <p className="text-lg font-bold leading-snug mb-2">
                  {translate(buttonName || "Add customer")}
                </p>
                <p className="text-xs opacity-75 leading-relaxed">
                  Search and add an existing customer to this campaign list.
                </p>
              </div>

              {/* Feature hints */}
              <ul className="text-xs opacity-60 space-y-1.5">
                {[
                  "Search by name or email",
                  "Instant results as you type",
                  "One customer per action",
                ].map((hint) => (
                  <li key={hint} className="flex items-center gap-1.5">
                    <svg
                      width="12"
                      height="12"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="white"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {hint}
                  </li>
                ))}
              </ul>
            </div>

            {/* ── Right Panel ── */}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p
                    className="text-xs font-semibold uppercase tracking-widest mb-1"
                    style={{ color: "rgb(26 86 219 / 0.9)" }}
                  >
                    Campaign List
                  </p>
                  <h3 className="text-xl font-bold text-gray-900">
                    Add a customer
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="w-9 h-9 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form
                onSubmit={handleAddCustomerList}
                className="flex flex-col gap-6"
              >
                {/* Search field */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {translate("customer")}
                  </label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={selectedCustomer}
                    loadOptions={loadOptions}
                    onChange={setSelectedCustomer}
                    placeholder="Search by name or email…"
                    noOptionsMessage={({ inputValue }) =>
                      inputValue
                        ? "No customers found"
                        : "Start typing to search"
                    }
                    loadingMessage={() => "Searching…"}
                    styles={selectStyles}
                    // Show email as subtitle in each option
                    formatOptionLabel={(option) => (
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {option.label}
                        </p>
                        {option.email && (
                          <p className="text-xs text-gray-400">
                            {option.email}
                          </p>
                        )}
                      </div>
                    )}
                  />
                  {!selectedCustomer && (
                    <p className="mt-1.5 text-xs text-gray-400">
                      Type at least one character to search your customer
                      database.
                    </p>
                  )}
                </div>

                {/* Selected customer preview */}
                {selectedCustomer && (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-100">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                      style={{ background: "rgb(26 86 219 / 0.9)" }}
                    >
                      {selectedCustomer.label?.charAt(0)?.toUpperCase() ?? "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {selectedCustomer.label}
                      </p>
                      {selectedCustomer.email && (
                        <p className="text-xs text-gray-400 truncate">
                          {selectedCustomer.email}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedCustomer(null)}
                      className="ml-auto text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0"
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}

                <div className="h-px bg-gray-100" />

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-800 transition-colors"
                  >
                    {translate("cancel")}
                  </button>

                  <SubmitButton
                    isLoading={isLoading}
                    textLoading={translate("saving") + "…"}
                    text={translate(buttonName || "add-customer")}
                    disabled={!selectedCustomer}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CampaignCustomerAddModal;
